
import { useRef, useEffect } from "react";
import { BotIcon, Loader2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import ChatMessage from "./ChatMessage";

interface Message {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
}

interface MessageListProps {
  messages: Message[];
  isLoadingPrompt: boolean;
  promptId: string | null;
}

const MessageList = ({ messages, isLoadingPrompt, promptId }: MessageListProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  if (messages.length === 0 && !isLoadingPrompt) {
    return (
      <ScrollArea className="flex-1 p-4">
        <div className="h-full flex items-center justify-center text-center text-muted-foreground p-8">
          <div>
            <BotIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-medium mb-2">Free-form Chat</h3>
            <p>Start typing to chat with the AI, or select a prompt from the sidebar to test specific templates.</p>
          </div>
        </div>
      </ScrollArea>
    );
  }

  if (messages.length === 0 && promptId && isLoadingPrompt) {
    return (
      <ScrollArea className="flex-1 p-4">
        <div className="h-full flex items-center justify-center text-center text-muted-foreground p-8">
          <div>
            <Loader2 className="w-8 h-8 mx-auto mb-4 text-primary animate-spin" />
            <h3 className="text-lg font-medium">Loading prompt...</h3>
          </div>
        </div>
      </ScrollArea>
    );
  }

  return (
    <ScrollArea className="flex-1 p-6">
      <div className="space-y-6">
        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}
      </div>
      <div ref={messagesEndRef} className="h-4" />
    </ScrollArea>
  );
};

export default MessageList;
